import { useState } from 'react';

import {
  FAQ,
  theme,
  useMediaMin,
  useWizardSteps,
  VerticalSpacer,
  WizardStep,
  WizardStepsProvider,
  WizardStepsState,
  WizardStepStatus,
} from '@foradevelopment/fora-ui';
import { AddEmploymentInput, EmploymentViewModelNew } from '@fora-monorepo/employer-my-pages-common/types';

import { AddEmployeeForm } from '../components/AddEmployeeForm';
import { AddEmploymentPeriodForm } from '../components/AddEmploymentPeriodForm';
import { useAppSelector } from '../../redux/hooks';
import { PersonDetails } from '../../components';

const addEmployeeStep = {
  stepName: 'add-employee-step',
  status: WizardStepStatus.Active,
  stepNumber: 1,
};
const addEmploymentStep = {
  stepName: 'add-employment-step',
  status: WizardStepStatus.Inactive,
  stepNumber: 2,
};

const initialState: WizardStepsState = {
  steps: {
    [addEmployeeStep.stepName]: {
      isOpen: true,
      ...addEmployeeStep,
    },
    [addEmploymentStep.stepName]: {
      isOpen: false,
      ...addEmploymentStep,
    },
  },
};

export const AddEmployeeWizard = ({
  goToNextPanel,
}: {
  goToNextPanel: (employment: EmploymentViewModelNew) => void;
}) => {
  return (
    <WizardStep.List>
      <WizardStepsProvider initialState={initialState}>
        <WizardSteps goToNextPanel={goToNextPanel} />
      </WizardStepsProvider>
    </WizardStep.List>
  );
};

const WizardSteps = ({ goToNextPanel }: { goToNextPanel: (employment: EmploymentViewModelNew) => void }) => {
  const { getStep, nextStep, openStep } = useWizardSteps();
  const faqsPersonInfo = useAppSelector(state => state.content.content.faqListAddEmployeePersonInfo);
  const faqsEmploymentPeriod = useAppSelector(state => state.content.content.faqListAddEmployeeEmploymentPeriod);
  const isDesktop = useMediaMin(theme.breakpoint.lg);
  const [addEmployeeInputData, setAddEmployeeInputData] = useState<AddEmploymentInput | undefined>();

  const goToAddEmploymentStep = (addEmploymentInput: AddEmploymentInput) => {
    setAddEmployeeInputData(addEmploymentInput);
    nextStep(addEmployeeStep.stepName);
  };

  return (
    <>
      <WizardStep
        testId={addEmployeeStep.stepName}
        status={getStep(addEmployeeStep.stepName).status}
        name={addEmployeeStep.stepName}
        number={addEmployeeStep.stepNumber}
        isSelectable={true}
        onActivate={() => {
          openStep(addEmployeeStep.stepName);
        }}
      >
        <WizardStep.Header onClick={() => openStep(addEmployeeStep.stepName)} testId="test-add-employee-step-title">
          Personuppgifter
        </WizardStep.Header>
        <WizardStep.Body>
          <WizardStep.MainSection>
            <AddEmployeeForm goToAddEmploymentStep={goToAddEmploymentStep} />
          </WizardStep.MainSection>
          <WizardStep.AsideSection>
            <>
              {faqsPersonInfo.length > 0 && isDesktop && (
                <FAQ testId="test-add-employee-faq" layout="side" questionAnswers={faqsPersonInfo} />
              )}
              {faqsPersonInfo.length > 0 && !isDesktop && (
                <FAQ testId="test-add-employee-faq" layout="modal" questionAnswers={faqsPersonInfo} />
              )}
            </>
          </WizardStep.AsideSection>
        </WizardStep.Body>
      </WizardStep>
      <WizardStep
        testId={addEmploymentStep.stepName}
        status={getStep(addEmploymentStep.stepName).status}
        name={addEmploymentStep.stepName}
        number={addEmploymentStep.stepNumber}
        isSelectable={true}
      >
        <WizardStep.Header onClick={() => openStep(addEmploymentStep.stepName)} testId="test-add-employment-step-title">
          Anställning
        </WizardStep.Header>
        <WizardStep.Body>
          <WizardStep.MainSection>
            {addEmployeeInputData?.identificationType && (
              <>
                <PersonDetails
                  firstName={addEmployeeInputData?.firstName ?? ''}
                  lastName={addEmployeeInputData?.lastName ?? ''}
                  identificationType={addEmployeeInputData?.identificationType ?? ''}
                  employeeId={addEmployeeInputData?.employeeId ?? ''}
                />
                <VerticalSpacer marginBottom={60} />
              </>
            )}
            {addEmployeeInputData && (
              <AddEmploymentPeriodForm
                goToConfirmationPanel={goToNextPanel}
                addEmploymentInputData={addEmployeeInputData}
              />
            )}
          </WizardStep.MainSection>
          <WizardStep.AsideSection>
            <>
              {faqsEmploymentPeriod.length > 0 && isDesktop && (
                <FAQ testId="test-add-employment-faq" layout="side" questionAnswers={faqsEmploymentPeriod} />
              )}
              {faqsEmploymentPeriod.length > 0 && !isDesktop && (
                <FAQ testId="test-add-employment-faq" layout="modal" questionAnswers={faqsEmploymentPeriod} />
              )}
            </>
          </WizardStep.AsideSection>
        </WizardStep.Body>
      </WizardStep>
    </>
  );
};
